export const OZON_AUTH_MESSAGE_SUCCESS = 'pixorion:ozon-auth:success' as const
export const OZON_AUTH_MESSAGE_ERROR = 'pixorion:ozon-auth:error' as const

export const OZON_AUTH_MODAL_ID = 'ozon-auth-modal'
export const OZON_AUTH_POPUP_NAME = 'pixorion_ozon_auth'
export const OZON_AUTH_STORAGE_KEY = 'pixorion:ozon-auth:pending'

export type OzonAuthSuccessMessage = {
    type: typeof OZON_AUTH_MESSAGE_SUCCESS
    access_token: string
    refresh_token: string
}

export type OzonAuthErrorMessage = {
    type: typeof OZON_AUTH_MESSAGE_ERROR
    error?: string
}

export type OzonAuthMessage = OzonAuthSuccessMessage | OzonAuthErrorMessage

const getAllowedMessageOrigin = (): string => {
    try {
        return new URL(__ENDPOINTS_HOST__).origin
    } catch {
        return ''
    }
}

export const getOzonLoginUrl = (): string => `${__ENDPOINTS_HOST__}/api/v1/auth/ozon/login`

export const openOzonAuthModal = (): void => {
    const checkbox = document.getElementById(OZON_AUTH_MODAL_ID) as HTMLInputElement | null
    if (checkbox) {
        checkbox.checked = true
    }
}

export const closeOzonAuthModal = (): void => {
    const checkbox = document.getElementById(OZON_AUTH_MODAL_ID) as HTMLInputElement | null
    if (checkbox) {
        checkbox.checked = false
    }
}

export const clearOzonAuthPending = (): void => {
    if (typeof window === 'undefined') {
        return
    }
    localStorage.removeItem(OZON_AUTH_STORAGE_KEY)
}

export const readOzonAuthPending = (): OzonAuthMessage | null => {
    if (typeof window === 'undefined') {
        return null
    }

    try {
        const raw = localStorage.getItem(OZON_AUTH_STORAGE_KEY)
        if (!raw) {
            return null
        }

        const parsed: unknown = JSON.parse(raw)
        if (!isRecord(parsed) || typeof parsed.type !== 'string') {
            return null
        }

        if (parsed.type === OZON_AUTH_MESSAGE_SUCCESS) {
            if (typeof parsed.access_token !== 'string' || typeof parsed.refresh_token !== 'string') {
                return null
            }
            return {
                type: OZON_AUTH_MESSAGE_SUCCESS,
                access_token: parsed.access_token,
                refresh_token: parsed.refresh_token,
            }
        }

        if (parsed.type === OZON_AUTH_MESSAGE_ERROR) {
            return {
                type: OZON_AUTH_MESSAGE_ERROR,
                error: typeof parsed.error === 'string' ? parsed.error : undefined,
            }
        }

        return null
    } catch {
        return null
    }
}

export const readOzonAuthTokensFromStorage = (): Pick<
    OzonAuthSuccessMessage,
    'access_token' | 'refresh_token'
> | null => {
    if (typeof window === 'undefined') {
        return null
    }

    const access_token = localStorage.getItem('access_token')
    const refresh_token = localStorage.getItem('refresh_token')

    if (!access_token || !refresh_token) {
        return null
    }

    return { access_token, refresh_token }
}

export type OzonAuthTokensSnapshot = {
    access_token: string | null
    refresh_token: string | null
}

export const readOzonAuthTokensSnapshot = (): OzonAuthTokensSnapshot => ({
    access_token: typeof window !== 'undefined' ? localStorage.getItem('access_token') : null,
    refresh_token: typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null,
})

export const hasNewOzonAuthTokens = (
    snapshot: OzonAuthTokensSnapshot | null
): Pick<OzonAuthSuccessMessage, 'access_token' | 'refresh_token'> | null => {
    const tokens = readOzonAuthTokensFromStorage()
    if (!tokens) {
        return null
    }

    if (!snapshot) {
        return null
    }

    if (tokens.access_token !== snapshot.access_token || tokens.refresh_token !== snapshot.refresh_token) {
        return tokens
    }

    return null
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

export const parseOzonAuthMessage = (event: MessageEvent): OzonAuthMessage | null => {
    const allowedOrigin = getAllowedMessageOrigin()
    if (!allowedOrigin || event.origin !== allowedOrigin) {
        return null
    }

    if (!isRecord(event.data) || typeof event.data.type !== 'string') {
        return null
    }

    if (event.data.type === OZON_AUTH_MESSAGE_SUCCESS) {
        if (typeof event.data.access_token !== 'string' || typeof event.data.refresh_token !== 'string') {
            return null
        }
        return {
            type: OZON_AUTH_MESSAGE_SUCCESS,
            access_token: event.data.access_token,
            refresh_token: event.data.refresh_token,
        }
    }

    if (event.data.type === OZON_AUTH_MESSAGE_ERROR) {
        return {
            type: OZON_AUTH_MESSAGE_ERROR,
            error: typeof event.data.error === 'string' ? event.data.error : undefined,
        }
    }

    return null
}
